import React, { ReactElement, Ref } from 'react';

import { defaultLabelGetter } from '../../helpers/defaultGetters';
import { defaultRenderItem } from '../../helpers/defaultRenderers';
import useAutocompleteControl from '../../hooks/useAutocompleteControl';
import { ControlContainer, ControlProps, SearchInput } from '../../layout';
import { GenericItem, GetInputProps } from '../../types';

export type AutoCompleteControlProps<TItem extends GenericItem> = Omit<
  ControlProps<TItem>,
  'as' | 'renderPlaceholder' | 'renderInput'
> & {
  getInputProps: GetInputProps;
} & {
  openMenu?: () => void;
} & {
  softLimit?: number;
  maxLength?: number;
  showCount?: boolean;
  countDisplayThreshold?: number;
};

const AutocompleteControlWrapped = <TItem extends GenericItem>(
  {
    isOpen,
    getInputProps,
    disabled,
    placeholder: placeholderProp,
    itemToString = defaultLabelGetter,
    selectedItem,
    openMenu,
    renderItem = defaultRenderItem,
    softLimit,
    maxLength,
    showCount,
    countDisplayThreshold,
    ...props
  }: AutoCompleteControlProps<TItem>,
  ref: Ref<HTMLInputElement>,
) => {
  const { containerProps, searchInputProps } = useAutocompleteControl({
    placeholderProp,
    isOpen,
    disabled,
    openMenu,
    selectedItem,
  });

  const showItemCount = !isOpen && selectedItem;

  return (
    <ControlContainer ref={ref} {...containerProps} {...props}>
      {showItemCount && renderItem({ selectedItem, itemToString, isOpen })}
      <SearchInput
        style={{ ...(showItemCount && { display: 'none' }) }}
        isOpen={isOpen}
        {...getInputProps?.(searchInputProps)}
        showIcon={isOpen}
        softLimit={softLimit}
        maxLength={maxLength}
        showCount={showCount}
        countDisplayThreshold={countDisplayThreshold}
      />
    </ControlContainer>
  );
};

const AutocompleteControl = React.forwardRef(AutocompleteControlWrapped);

AutocompleteControl.displayName = 'AutocompleteControl';

export default AutocompleteControl as <T extends GenericItem>(
  p: AutoCompleteControlProps<T> & {
    ref?: Ref<HTMLDivElement>;
  },
) => ReactElement;
